import React from 'react';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Avatar } from '@components/atoms/Avatar';
import { ButtonLink } from '@components/atoms/ButtonLink';
import { Employee } from '@models/employee.model';
import { UserOrganizationRoleStatus, UserRoles } from '@models/user.model';
import { format } from 'date-fns';
import formatCurrency from '@utils/formatCurrency';
import { Badge } from '@components/atoms/Badge';
import { PayPeriod } from '@pages/users/constants/constants';
import { Mode } from '@pages/users/constants/constants';
import { canManageUserRole, getTranslatedRoleLabel } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';

const STATUS_BADGE_CONFIG: Record<UserOrganizationRoleStatus, { variant: 'success' | 'error' | 'warning' | 'secondary'; label: string }> = {
  [UserOrganizationRoleStatus.ACTIVE]: { variant: 'success', label: 'Active' },
  [UserOrganizationRoleStatus.DISABLED]: { variant: 'error', label: 'Disabled' },
  [UserOrganizationRoleStatus.READ_ONLY]: { variant: 'warning', label: 'Read Only' },
  [UserOrganizationRoleStatus.OVERDUE]: { variant: 'secondary', label: 'Overdue' },
};

interface UserCardProps {
  showRole?: boolean;
  users?: Employee[];
  onEditUser?: (employee: Employee) => void;
  onChangeStatus?: (employee: Employee) => void;
  /** Called when the user clicks anywhere on the card body. */
  onSelectUser?: (employee: Employee) => void;
  isOwner?: boolean;
  /** Role of the signed-in user, used to gate editing by hierarchy. */
  actorRole?: string | null;
  isReadOnly?: boolean;
}

export const UserCard: React.FC<UserCardProps> = ({
  users,
  showRole,
  onEditUser,
  onChangeStatus,
  onSelectUser,
  isOwner = false,
  actorRole,
  isReadOnly = false,
}) => {
  // Sprint 29, story-148 (also closes Addendum 9's blank-for-OWNER/SUPER_ADMIN defect — see
  // getTranslatedRoleLabel's own comment in roleHierarchy.ts).
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');

  const formatDate = (value?: string | Date | null): string => {
    if (!value) return 'N/A';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (!(date instanceof Date) || isNaN(date.getTime())) return 'N/A';
    return format(date, 'MM/dd/yyyy');
  };

  return (
    <div className="mt-4 h-full">
      {users && users.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 pb-6 mt-4">
          {(users || []).map((employee) => (
            <Card
              key={employee.employeeId}
              onClick={
                onSelectUser ? () => onSelectUser(employee) : undefined
              }
              className={`w-full flex flex-col h-full justify-between ${
                onSelectUser ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
              }`}>
              <div className="relative flex gap-2 justify-between">
                <div className="flex gap-2 flex-col">
                  <div className="flex gap-2 items-center">
                    <Avatar
                      size="2xl"
                      src={employee.user?.avatar}
                      text={employee.user?.firstName}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100 capitalize">
                      {employee.user?.firstName} {employee.user?.lastName}
                    </div>
                    {employee.user?.organizationRoleStatus && (
                      <Badge
                        size="sm"
                        variant={STATUS_BADGE_CONFIG[employee.user.organizationRoleStatus]?.variant || 'secondary'}>
                        {STATUS_BADGE_CONFIG[employee.user.organizationRoleStatus]?.label || employee.user.organizationRoleStatus}
                      </Badge>
                    )}
                  </div>
                  {showRole && (
                    <Badge className="capitalize">
                      {getTranslatedRoleLabel(employee?.user?.role, tEnumLabels, tCommon.unknown)}
                    </Badge>
                  )}
                  {(employee.user?.role === UserRoles.EMPLOYEE ||
                    employee.user?.role === UserRoles.CREW_LEAD) && (
                    <div className="mt-1 flex flex-col gap-1">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Crews
                      </p>
                      {employee.crews && employee.crews.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {employee.crews.map((crew) => (
                            <Badge
                              key={crew.crewId}
                              size="sm"
                              variant="primary"
                              className="capitalize">
                              {crew.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-300">
                          Not assigned to a crew
                        </span>
                      )}
                    </div>
                  )}
                  {employee.user?.organizationRoles && employee.user.organizationRoles.length > 0 && (
                    <div className="mt-3 flex flex-col gap-2">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Organizations & Roles
                      </p>
                      <div className="flex flex-col gap-2 max-h-[120px] overflow-y-auto">
                        {employee.user.organizationRoles.map((orgRole, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-lg px-3 py-2 border border-blue-100 dark:border-gray-600"
                          >
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {orgRole.organization.name}
                              </span>
                              <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                                Role: {getTranslatedRoleLabel(orgRole.role, tEnumLabels, tCommon.unknown)}
                              </span>
                            </div>
                            <Badge variant="primary" className="ml-2 shrink-0 capitalize">
                              {getTranslatedRoleLabel(orgRole.role, tEnumLabels, tCommon.unknown)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div
                    className="flex flex-col"
                    onClick={(e) => e.stopPropagation()}>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      Email
                    </p>
                    <ButtonLink
                      className="text-sm text-gray-500 dark:text-gray-300 truncate max-w-full sm:max-w-[200px] lg:max-w-[250px] "
                      href={`mailto:${employee.user?.email || ''}`}>
                      {employee.user?.email}
                    </ButtonLink>
                  </div>
                </div>
                <div
                  className="absolute top-0 right-0 flex gap-1 h-10"
                  onClick={(e) => e.stopPropagation()}>
                  {isOwner && onChangeStatus && (
                    <Button
                      variant="tertiary"
                      onlyIcon
                      onClick={() => onChangeStatus(employee)}
                      title="Change Status">
                      <SettingsOutlinedIcon sx={{ fontSize: '19px' }} />
                    </Button>
                  )}
                  {/* Only editable at or below the signed-in user's level. The
                      server enforces this too; this keeps the affordance honest
                      even while the cached list lags a role change. */}
                  {!isReadOnly &&
                    onEditUser &&
                    canManageUserRole(actorRole, employee.user?.role) && (
                      <Button
                        variant="tertiary"
                        onlyIcon
                        onClick={() => onEditUser(employee)}>
                        <ModeEditOutlinedIcon sx={{ fontSize: '19px' }} />
                      </Button>
                    )}
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-3">
                <div className="flex justify-between gap-2 mt-3">
                  {employee.dob && (
                    <div className="flex flex-col">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        DOB
                      </p>
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {formatDate(employee.dob)}
                      </div>
                    </div>
                  )}
                  {employee.hireDate && (
                    <div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        Hire Date
                      </p>
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {formatDate(employee.hireDate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-3">
                <div className="flex justify-between gap-2 mt-3">
                  {employee.rate && (
                    <div className="flex flex-col">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        Rate
                      </p>
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {employee.rate
                          ? formatCurrency(employee.rate) + ' / hr'
                          : 'N/A'}
                      </div>
                    </div>
                  )}
                  {employee.mode && (
                    <div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        Mode
                      </p>
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {Mode[employee.mode as keyof typeof Mode] || 'N/A'}
                      </div>
                    </div>
                  )}
                  {employee.payPeriod && (
                    <div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        Pay Period
                      </p>
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {PayPeriod[
                          employee.payPeriod as keyof typeof PayPeriod
                        ] || 'N/A'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
